import {
  ArrowRightOutlined,
  CopyOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Line } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { PromoterModel, PromoterTypes } from '@mm/state/model/PromoterModel';
import { StoreState } from '@mm/state/store-interfaces';
import { ChatWidgetTroubleshootingTool } from '@mm/stateful-components/ChatWidget';
import googleTagManager from 'assets/google-tag-manager.svg';
import settings from 'assets/settings.svg';
import shopify from 'assets/shopify-icon.png';
import bigCommerce from 'assets/big-commerce.svg';
import wordpress from 'assets/wordpress-logo.svg';
import classNames from 'classnames';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { EMAIL_MATCH_REGEX } from '../../../../common/constants/validations';
import { successToast } from '../../../../common/helpers/toast';
import { EmailInput } from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { MOBILE_SMALL } from '../../../../editor/constants/screenSize';
import {
  useCustomPromoterJSCodes,
  usePromoterJSCode,
} from '../../../../editor/features/startBotPromoters/common/use-promoter-js-code';
import { useSetNextOnboardingStep } from '../../SignupOnboarding/use-set-next-onboarding-step';
import { HowXRayWorks } from '../HowXRayWorks/HowXRayWorks';
import DesktopIconSrc from './desktop-icon.svg';
import styles from './InstallationPage.module.css';

import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';
import {
  KnownSiteType,
  KNOWN_SITE_TYPES,
} from '@mm/stateful-components/ChatWidget/InstallationGuide/constants';
import { SIGNUP } from '../../../../common/helpers/plugins/constants';
import { trackFacebookEvent } from '../../../../common/helpers/plugins/facebook-pixel-init';
import { trackSignUpEventGA } from '../../../../modules/MarketingAnalytics';
import { OnboardingLogosPanel } from '../OnboardingLogosPanel/OnboardingLogosPanel';
import { ContactSupportButtons } from './ContactSupportButtons/ContactSupportButtons';
import {
  XRayLeadsTour,
  XRayLeadsTourRefs,
} from '../../../../editor/features/beta/modules/GetLeads/XRay/XRayLeadsTour';
import { SuperpixelSection } from '../../../../editor/features/beta/modules/Integrations/NativeIntegrations/components/XRayIngegrations/SuperpixelSection/SuperpixelSection';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

type Props = {
  css?: Partial<{
    container?: string;
    diagnosticResultsContainer: string;
    title?: string;
    thirdStepButton?: string;
    stepsContainer?: string;
    step?: string;
  }>;
  botId;
  promoterId?: string | number;
  pageRemoteId?: string;
  title?: string;
  badge?: ReactNode;
  subtitle?: string;
  canSkip?: boolean;
  thirdStepConfig?: {
    title?: string;
    onClick?: () => void;
    button?: string;
  };
  canCloseResults?: boolean;
  shouldEngage?: boolean;
  withVideo?: boolean;
  openTour?: boolean;
};

// tslint:disable-next-line:cyclomatic-complexity
export const InstallationPageForm = ({
  css,
  botId,
  title,
  badge,
  subtitle = 'This allows us to gather contacts from your traffic',
  promoterId,
  pageRemoteId,
  canSkip = true,
  thirdStepConfig,
  canCloseResults = false,
  shouldEngage = false,
  withVideo,
  openTour,
}: Props) => {
  const promoter = useSelector((state: StoreState) =>
    state.promoter.entities.find(
      promoter => promoter.id?.toString() === promoterId?.toString(),
    ),
  );
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const tourRefs = useRef<XRayLeadsTourRefs>(null);
  const [email, setEmail] = useState('');
  const [engagedWithAPage, setEngagedWithAPage] = useState(false);

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const { pixelName: whitelabelPixelName } = useWhitelabelConfig();
  const pixelName = whitelabelPixelName || 'X-Ray Pixel';
  const leadGenPixelName = whitelabelPixelName
    ? `${whitelabelPixelName} Lead Generator`
    : 'X-Ray Lead Generation Pixel';

  const { showSlideoutMenu } = useViewActions();
  const { create, update } = usePromoterCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const dispatch = useDispatch();

  const registerSignUpEvent = () => {
    trackFacebookEvent(SIGNUP);
    trackSignUpEventGA();
    setNextOnboardingStep(OnboardingSignupStep.NONE);
  };

  const promoterJSCode = usePromoterJSCode(promoter);
  const promoterJSCode2Map = useCustomPromoterJSCodes(promoter);

  const registerEngagement = () => {
    setEngagedWithAPage(true);
  };

  const openInstallationGuide = (siteType: KnownSiteType) => {
    showSlideoutMenu('customerChatInstallationGuide', {
      siteType,
      promoterJSCode,
      promoterJSCode2: promoterJSCode2Map[siteType],
      promoterType: PromoterTypes.GETEMAILS,
      onCopyClick: registerEngagement,
    });
  };

  const isShopify = useSelector(selectIsShopifyContext);

  const buttons = useMemo(
    () =>
      [
        {
          icon: googleTagManager,
          name: 'Tags Manager ',
          siteType: KNOWN_SITE_TYPES[7],
        },
        {
          icon: wordpress,
          name: 'WordPress',
          siteType: KNOWN_SITE_TYPES[0],
          onClick: () => {
            registerEngagement();
          },
        },
        {
          icon: shopify,
          name: 'Shopify',
          siteType: KNOWN_SITE_TYPES[2],
        },
        {
          icon: bigCommerce,
          name: 'BigCommerce',
          siteType: KNOWN_SITE_TYPES[8],
        },
        {
          icon: settings,
          name: 'Manually install',
          siteType: KNOWN_SITE_TYPES[9],
        },
      ].filter(opt =>
        isShopify
          ? ['shopify', 'manually'].some(k =>
              opt.name.toLowerCase().includes(k),
            )
          : true,
      ),
    [isShopify],
  );

  const updatePromoter = async () => {
    if (promoter) {
      await update(botId, {
        ...promoter,
        sendToDev: email,
      });
      registerEngagement();
    }
  };

  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const onAfterTestButtonClick = () => {
    setEngagedWithAPage(true);
    dispatch({
      type: 'CHATWIDGETTROUBLESHOOTING__NO_ISSUES_UNMOUNT',
    });
  };

  const onTestClose = () => {
    if (canCloseResults && activeFacebookPage?.id) {
      connectedPageCRUDActions.retrievePage(activeFacebookPage.id);
    }
  };

  const createPromoter = async () => {
    await create({
      botId,
      name: leadGenPixelName,
      type: PromoterTypes.GETEMAILS,
      active: false,
      ...(featureFlags.compliant_klaviyo_app && { myContactsOnly: true }),
    } as PromoterModel);
  };

  useEffect(() => {
    if (!promoterId) {
      createPromoter();
    }
  }, []);

  return (
    <div
      className={classNames(
        styles.installationBlock,
        styles.column,
        css?.container,
      )}
    >
      <XRayLeadsTour ref={tourRefs} open={openTour} />
      <div className={classNames(styles.title, css?.title)}>
        {badge && <span className={styles.badge}>{badge}</span>}

        <div className={styles.heading}>
          {title || `Install ${leadGenPixelName} on your Website`}
        </div>
        {!isMobile && subtitle && <div className={styles.text}>{subtitle}</div>}
      </div>

      {isMobile && (
        <div className={classNames(styles.mobileBlock, styles.column)}>
          <div className={classNames(styles.info, styles.row)}>
            <img src={DesktopIconSrc} className={styles.desktopIcon} />
            <div className={styles.column}>
              <div className={styles.heading}>
                Our platform is best used on a desktop
              </div>
              <div className={styles.text}>
                We will email you directions on how to install X-Ray Lead
                Generation Pixel.
              </div>
            </div>
          </div>

          <div className={classNames(styles.mobileInstallation, styles.column)}>
            <div className={classNames(styles.emailBlock, styles.column)}>
              <div className={styles.heading}>Email directions:</div>
              <EmailInput
                inputProps={{
                  placeholder: '',
                  style: { border: '1px solid #D9D9D9', minHeight: '40px' },
                }}
                onChange={p => setEmail(p.email)}
                className={styles.input}
                withIcon={false}
                strict
              />
              <PrimaryButton
                className={styles.button}
                onClick={() => updatePromoter()}
                disabled={!email || !EMAIL_MATCH_REGEX.test(email)}
              >
                <SendOutlined />
                Send
              </PrimaryButton>
            </div>
            <Line className={styles.separator} text="or" />
            <CopyToClipboard
              text={promoterJSCode}
              onCopy={() =>
                successToast('Instructions are copied.', 'top-right')
              }
            >
              <PrimaryButton className={styles.primaryButton}>
                <CopyOutlined />
                Copy Installation Instructions
              </PrimaryButton>
            </CopyToClipboard>
          </div>
        </div>
      )}

      {!isMobile && (
        <div
          className={classNames(
            styles.stepsContainer,
            styles.column,
            css?.stepsContainer,
          )}
        >
          <div className={classNames(styles.row, styles.videoRow, css?.step)}>
            <div className={styles.step}>
              <div className={styles.heading}>
                1. Install {pixelName} on your website following the instruction
              </div>
              <div
                className={classNames(
                  styles.instructionButtons,
                  styles.row,
                  styles.text,
                )}
                ref={tourRefs?.current?.installationGuideRef}
              >
                {buttons.map(button => (
                  <div
                    key={button.name}
                    className={classNames(
                      styles.instructionButton,
                      styles.column,
                    )}
                    onClick={() => {
                      if (button?.onClick) {
                        button.onClick();
                      }
                      openInstallationGuide(button.siteType);
                    }}
                  >
                    <img src={button.icon} className={styles.logo} />
                    <div className={styles.name}>{button.name}</div>
                  </div>
                ))}
              </div>
            </div>
            {withVideo && (
              <iframe
                width="380"
                height="200"
                src="https://player.vimeo.com/video/825855525?h=e882c0cd95&badge=0&autopause=0&player_id=0&app_id=58479"
                allowFullScreen
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={`${leadGenPixelName} Email Capture System in 90 Seconds`}
              />
            )}
          </div>

          <div className={classNames(styles.step, css?.step)}>
            <div
              className={styles.heading}
              ref={tourRefs.current?.verifyInputRef}
            >
              2. Verify the script is working correctly
              <ChatWidgetTroubleshootingTool
                promoter={promoter}
                remotePageId={pageRemoteId}
                botIdOverride={botId}
                css={{
                  textInput: styles.input,
                  button: styles.diagnosticButton,
                  diagnosticContainer: classNames(
                    styles.diagnosticResultContainer,
                    css?.diagnosticResultsContainer,
                  ),
                }}
                canCloseResults={canCloseResults}
                showOkButton={canCloseResults}
                onAfterButtonClick={onAfterTestButtonClick}
                onClose={onTestClose}
                buttonName="Test It"
                withHeading={false}
                usedInSlideout
                isXRay
              />
            </div>
          </div>
          <SuperpixelSection onBeforeTitle={<span>3.</span>} />
          {((shouldEngage && engagedWithAPage && thirdStepConfig) ||
            (!shouldEngage && thirdStepConfig)) && (
            <div className={classNames(styles.step, css?.step)}>
              <div className={styles.heading}>
                4. {thirdStepConfig.title}
                <Button
                  ref={tourRefs.current?.triggerEditorRef}
                  css={{
                    container: classNames(
                      styles.primaryButton,
                      styles.next,
                      css?.thirdStepButton,
                    ),
                  }}
                  onClick={() => thirdStepConfig?.onClick?.()}
                >
                  <ArrowRightOutlined className={styles.arrowIcon} />
                  {thirdStepConfig.button}
                </Button>
              </div>
            </div>
          )}

          {!isShopify && promoter && (
            <ContactSupportButtons
              promoter={promoter}
              onClick={registerEngagement}
              onSkipClick={registerSignUpEvent}
              className={css?.step}
              canSkip={canSkip}
            />
          )}
        </div>
      )}
    </div>
  );
};

export const InstallationPage = () => {
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const { userAttributes } = useSelector(selectSessionSelector);
  const { openNewBillingModal2023 } = useModalActions();
  const { retrieveAll } = usePromoterCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const registerSignUpEvent = () => {
    trackFacebookEvent(SIGNUP);
    trackSignUpEventGA();
    setNextOnboardingStep(OnboardingSignupStep.NONE);
  };
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const facebookPage = useSelector(
    (state: StoreState) =>
      state.connectedPage.entities[0] || activeFacebookPageSelector(state),
  );

  const [promoterId, setPromoterId] = useState<number | undefined>();

  const retrieveGetemailPromoters = async () => {
    if (!facebookPage.activeBotId || facebookPage.subscription) {
      registerSignUpEvent();
      return;
    }

    if (facebookPage?.activeBotId) {
      const res = await retrieveAll(facebookPage?.activeBotId, 'getemails');
      if (res?.length) {
        const firstPromoter = res?.[0];
        setPromoterId(firstPromoter.id);
      } else {
        registerSignUpEvent();
      }
    }
  };

  useEffect(() => {
    retrieveGetemailPromoters();
  }, []);

  useEffect(() => {
    if (!userAttributes.seenNewBilling2023Modal && !facebookPage.subscription) {
      openNewBillingModal2023({
        landmineName: landmineNames['INSTALLATION-PAGE-UPGRADE'],
        withCloseButton: false,
        hideFreePlan: false,
      });
    }
  }, [
    userAttributes.seenNewBilling2023Modal,
    facebookPage.subscription,
    openNewBillingModal2023,
  ]);

  return (
    <div className={classNames(styles.container, styles.row)}>
      {!isMobile && <OnboardingLogosPanel className={styles.left} />}

      <div className={classNames(styles.right, styles.column)}>
        {!isMobile && (
          <HowXRayWorks
            header={`How ${companyName} website visitor works`}
            css={{
              container: styles.howXRayWorksContainer,
              content: styles.howXRayWorksContent,
              heading: styles.howXRayWorksHeading,
            }}
            iconColor="#7087A9"
          />
        )}

        {promoterId && (
          <InstallationPageForm
            css={{
              diagnosticResultsContainer: styles.diagnosticResultsContainer,
            }}
            promoterId={promoterId}
            botId={facebookPage?.activeBotId}
            pageRemoteId={facebookPage.remoteId}
            shouldEngage
            thirdStepConfig={{
              title: 'Proceed with automation',
              onClick: registerSignUpEvent,
              button: 'Next',
            }}
          />
        )}
      </div>
    </div>
  );
};
